// Catálogo — the coordinator maintains the estudios/servicios/precios the agent
// quotes via lookup_price. Composes the locked design system; hardcoded es-MX
// copy per the pages convention.

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import {
  type CatalogItem,
  createCatalogItem,
  getCatalog,
  updateCatalogItem,
} from './catalog-data.js'
import {
  Button,
  Chip,
  DataTable,
  type DataTableColumn,
  EmptyStateCard,
  FormField,
  Input,
  Modal,
  NumberInput,
  PageHeader,
  Select,
  Spinner,
  Textarea,
  toast,
} from '../../design-system/index.js'
import { QueryError } from '../../lib/QueryError.js'

const mxn = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' })

export function CatalogPage(): JSX.Element {
  const qc = useQueryClient()
  const {
    data: items,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['catalog'],
    queryFn: getCatalog,
  })
  const [editing, setEditing] = useState<CatalogItem | null>(null)
  const [creating, setCreating] = useState(false)

  const invalidate = (): void => void qc.invalidateQueries({ queryKey: ['catalog'] })

  if (isError) {
    return (
      <div data-testid="catalog">
        <QueryError message="No pudimos cargar el catálogo." onRetry={() => void refetch()} />
      </div>
    )
  }
  if (isLoading || !items) {
    return (
      <div data-testid="catalog">
        <Spinner label="Cargando catálogo…" />
      </div>
    )
  }

  async function toggleActive(item: CatalogItem): Promise<void> {
    try {
      await updateCatalogItem(item.id, { active: !item.active })
      toast.ok(item.active ? 'Elemento desactivado' : 'Elemento activado')
      invalidate()
    } catch {
      toast.error('No se pudo actualizar')
    }
  }

  const columns: DataTableColumn<CatalogItem>[] = [
    {
      key: 'name',
      label: 'Elemento',
      render: (i) => <span className="font-medium">{i.name}</span>,
    },
    { key: 'category', label: 'Categoría', render: (i) => i.category ?? '—' },
    { key: 'price', label: 'Precio', align: 'right', render: (i) => mxn.format(i.priceMxn) },
    {
      key: 'status',
      label: 'Estado',
      render: (i) => (
        <Chip tone={i.active ? 'ok' : 'draft'} dot>
          {i.active ? 'Activo' : 'Inactivo'}
        </Chip>
      ),
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: (i) => (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={() => setEditing(i)}>
            Editar
          </Button>
          <Button size="sm" variant="ghost" onClick={() => void toggleActive(i)}>
            {i.active ? 'Desactivar' : 'Activar'}
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div data-testid="catalog" className="mx-auto w-full max-w-5xl">
      <PageHeader
        title="Catálogo"
        subtitle="Estudios, servicios y precios que el agente puede cotizar."
        actions={
          <Button size="sm" onClick={() => setCreating(true)}>
            Nuevo elemento
          </Button>
        }
      />
      {items.length === 0 ? (
        <EmptyStateCard
          icon={<span aria-hidden>🏷️</span>}
          kind="day1"
          title="Aún no hay elementos"
          body="Agrega estudios o servicios con su precio para que el agente los cotice."
          actions={
            <Button size="sm" onClick={() => setCreating(true)}>
              Nuevo elemento
            </Button>
          }
        />
      ) : (
        <DataTable caption="Catálogo" columns={columns} rows={items} />
      )}

      {creating && (
        <ItemModal
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false)
            invalidate()
          }}
        />
      )}
      {editing && (
        <ItemModal
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            invalidate()
          }}
        />
      )}
    </div>
  )
}

function ItemModal({
  item,
  onClose,
  onSaved,
}: {
  item?: CatalogItem
  onClose: () => void
  onSaved: () => void
}): JSX.Element {
  const [name, setName] = useState(item?.name ?? '')
  const [category, setCategory] = useState(item?.category ?? '')
  const [priceMxn, setPriceMxn] = useState(item?.priceMxn ?? 0)
  const [description, setDescription] = useState(item?.description ?? '')
  const [active, setActive] = useState<boolean>(item?.active ?? true)
  const [busy, setBusy] = useState(false)
  const isEdit = Boolean(item)

  async function submit(): Promise<void> {
    if (!name.trim()) return
    setBusy(true)
    try {
      if (item) {
        await updateCatalogItem(item.id, {
          name: name.trim(),
          category: category.trim() || null,
          priceMxn,
          description: description.trim() || null,
          active,
        })
      } else {
        await createCatalogItem({
          name: name.trim(),
          priceMxn,
          ...(category.trim() ? { category: category.trim() } : {}),
          ...(description.trim() ? { description: description.trim() } : {}),
        })
      }
      toast.ok(isEdit ? 'Elemento actualizado' : 'Elemento agregado')
      onSaved()
    } catch {
      toast.error('No se pudo guardar', { description: 'Inténtalo de nuevo.' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? 'Editar elemento' : 'Nuevo elemento'}
      subtitle={
        isEdit ? 'Actualiza el estudio o servicio.' : 'Agrega un estudio o servicio con su precio.'
      }
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button className="ml-auto" onClick={() => void submit()} disabled={busy}>
            {busy ? 'Guardando…' : isEdit ? 'Guardar' : 'Agregar'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3.5">
        <FormField label="Nombre" htmlFor="cat-name" required>
          <Input id="cat-name" value={name} onChange={(e) => setName(e.target.value)} />
        </FormField>
        <FormField label="Categoría" htmlFor="cat-cat" required="optional">
          <Input
            id="cat-cat"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="Estudio, Servicio, Consulta…"
          />
        </FormField>
        <FormField label="Precio (MXN)" htmlFor="cat-price">
          <NumberInput
            id="cat-price"
            value={priceMxn}
            onChange={setPriceMxn}
            min={0}
            currency="MXN"
          />
        </FormField>
        <FormField label="Descripción" htmlFor="cat-desc" required="optional">
          <Textarea
            id="cat-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </FormField>
        {isEdit && (
          <FormField label="Estado" htmlFor="cat-status">
            <Select
              id="cat-status"
              options={[
                { value: 'active', label: 'Activo' },
                { value: 'inactive', label: 'Inactivo' },
              ]}
              value={active ? 'active' : 'inactive'}
              onChange={(e) => setActive(e.target.value === 'active')}
            />
          </FormField>
        )}
      </div>
    </Modal>
  )
}
